import { AttrKeyValue } from '../../types/attributes';
import {
  ATTR_NAMES,
  getAttrFromProduct,
  getAttrsFromProductByNamePrefix,
} from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { RatingContext } from '../../utils/analytics';
import { GoogleRatingData, GoogleReviewData, GoogleReviewItem } from './types';

const REVIEW_NAME_PREFIX = 'GOOGLE_REVIEW_';

const makeGetValue =
  (attribute: AttrKeyValue | undefined) =>
  (key: string): string | undefined =>
    attribute?.value?.find((entry) => entry.key?.trim() === key)?.values?.[0]
      ?.value;

const parseScore = (raw: string | undefined): number | undefined => {
  const parsed = parseFloat((raw ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getReviews = (product: CmsProduct): GoogleReviewItem[] => {
  const attributesByNumber = new Map<number, AttrKeyValue>();
  for (const attribute of getAttrsFromProductByNamePrefix(
    REVIEW_NAME_PREFIX,
    'ComponentAttributesAttrKeyValue',
    product,
  )) {
    const n = parseInt(
      (attribute.name?.name ?? '').slice(REVIEW_NAME_PREFIX.length),
      10,
    );
    if (Number.isInteger(n) && !attributesByNumber.has(n)) {
      attributesByNumber.set(n, attribute);
    }
  }

  // Per CMS-1765 reviews are contiguous from _1 (PCT guarantees no gaps): read
  // them in numeric order and stop at the first missing number.
  const reviews: GoogleReviewItem[] = [];
  for (let n = 1; attributesByNumber.has(n); n += 1) {
    const getValue = makeGetValue(attributesByNumber.get(n));
    reviews.push({
      reviewName: getValue('review_name'),
      reviewScore: parseScore(getValue('review_score')),
      reviewDate: getValue('review_date'),
      reviewComment: getValue('review_comment'),
    });
  }

  return reviews;
};

export const getGoogleLink = (product: CmsProduct): string | undefined =>
  getAttrFromProduct(ATTR_NAMES.GOOGLE_LINK, product)?.value?.trim() ||
  undefined;

export const getGoogleRating = (
  product: CmsProduct,
): GoogleRatingData | null => {
  const getValue = makeGetValue(
    getAttrFromProduct(ATTR_NAMES.GOOGLE_RATINGS, product),
  );

  const rateScore = parseScore(getValue('rate_score'));
  if (rateScore === undefined) return null;

  const rawCount = parseInt(
    (getValue('rate_count') ?? '').replace(/[^\d]/g, ''),
    10,
  );
  const rateCount =
    Number.isFinite(rawCount) && rawCount > 0 ? rawCount : undefined;

  const rateBadgeLabel = getValue('rate_badge_label')?.trim() || undefined;

  return { rateScore, rateCount, rateBadgeLabel };
};

export const getGoogleReviewData = (
  product: CmsProduct,
): GoogleReviewData | null => {
  const rating = getGoogleRating(product);
  if (!rating) return null;

  return {
    ...rating,
    reviews: getReviews(product),
  };
};

export const getGoogleRatingContext = (
  rating: GoogleRatingData,
): RatingContext => ({
  source: 'Google',
  score: rating.rateScore,
  count: rating.rateCount,
});
